import React from 'react';
import { Modal, Table, Tag, Card, Row, Col, Statistic, Divider, Button } from 'antd';
import { FaEye, FaDownload } from 'react-icons/fa';

const ReviewModalAntD = ({ 
  showReviewModal, 
  setShowReviewModal, 
  moneyReport, 
  counterData,
  exportReviewToPDF 
}) => {

  // Currency Denominations Table
  const currencyColumns = [
    {
      title: 'Denomination',
      dataIndex: 'label',
      key: 'label',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      key: 'qty',
      align: 'center',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (val) => `฿${val.toLocaleString()}`,
    },
  ];

  const currencyData = [
    { key: 'note1000', label: '฿1000', qty: moneyReport.denominations.note1000?.qty || 0, amount: moneyReport.denominations.note1000?.total || 0 },
    { key: 'note500', label: '฿500', qty: moneyReport.denominations.note500?.qty || 0, amount: moneyReport.denominations.note500?.total || 0 },
    { key: 'note100', label: '฿100', qty: moneyReport.denominations.note100?.qty || 0, amount: moneyReport.denominations.note100?.total || 0 },
    { key: 'note50', label: '฿50', qty: moneyReport.denominations.note50?.qty || 0, amount: moneyReport.denominations.note50?.total || 0 },
    { key: 'note20', label: '฿20', qty: moneyReport.denominations.note20?.qty || 0, amount: moneyReport.denominations.note20?.total || 0 },
    { key: 'note10', label: '฿10', qty: moneyReport.denominations.note10?.qty || 0, amount: moneyReport.denominations.note10?.total || 0 },
    { key: 'note5', label: '฿5', qty: moneyReport.denominations.note5?.qty || 0, amount: moneyReport.denominations.note5?.total || 0 },
    { key: 'note1', label: '฿1', qty: moneyReport.denominations.note1?.qty || 0, amount: moneyReport.denominations.note1?.total || 0 },
  ];

  const totalCash = Object.entries(moneyReport.denominations)
    .reduce((sum, [key, value]) => {
      if (key === 'photo') return sum;
      return sum + (value.total || 0);
    }, 0);

  // Coins Table
  const coinColumns = currencyColumns;

  const coinData = [
    { key: 'coin10', label: '฿10', qty: moneyReport.coins.coin10?.qty || 0, amount: moneyReport.coins.coin10?.total || 0 },
    { key: 'coin5', label: '฿5', qty: moneyReport.coins.coin5?.qty || 0, amount: moneyReport.coins.coin5?.total || 0 },
    { key: 'coin2', label: '฿2', qty: moneyReport.coins.coin2?.qty || 0, amount: moneyReport.coins.coin2?.total || 0 },
    { key: 'coin1', label: '฿1', qty: moneyReport.coins.coin1?.qty || 0, amount: moneyReport.coins.coin1?.total || 0 },
  ];

  const totalCoins = Object.entries(moneyReport.coins)
    .reduce((sum, [key, value]) => sum + (value.total || 0), 0);

  // Payment Methods Table
  const paymentColumns = [
    { title: 'Item', dataIndex: 'item', key: 'item', render: (text) => <strong>{text}</strong> },
    { title: 'Manual Entry', dataIndex: 'manual', key: 'manual', align: 'right', render: (val) => `฿${val.toLocaleString()}` },
    { title: 'Expenses', dataIndex: 'expenses', key: 'expenses', align: 'right', render: (val) => `฿${val.toLocaleString()}` },
    { title: 'System Calculated', dataIndex: 'system', key: 'system', align: 'right', render: (val) => `฿${val.toLocaleString()}` },
    { 
      title: 'Difference', 
      dataIndex: 'difference', 
      key: 'difference', 
      align: 'right', 
      render: (val) => (
        <span style={{ color: val === 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
          ฿{Math.abs(val).toLocaleString()}
        </span>
      )
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status', 
      align: 'center',
      render: (val) => (
        <Tag color={val === 0 ? 'success' : 'error'}>
          {val === 0 ? '✓ Match' : '✗ Mismatch'}
        </Tag>
      )
    },
  ];

  const cashDiff = (totalCash + (moneyReport.expenses || 0)) - (counterData.paymentBreakdown.cash.amount || 0);
  const cardDiff = ((moneyReport.creditCard || 0) + (moneyReport.cashOutCard || 0)) - (counterData.paymentBreakdown.card.amount || 0);
  const transferDiff = ((moneyReport.transfer || 0) + (moneyReport.cashOutTE || 0)) - (counterData.paymentBreakdown.btransfer.amount || 0);

  const paymentData = [
    {
      key: 'cash',
      item: 'Cash in System',
      manual: totalCash,
      expenses: moneyReport.expenses || 0,
      system: counterData.paymentBreakdown.cash.amount || 0,
      difference: cashDiff,
      status: cashDiff
    },
    {
      key: 'card',
      item: 'Credit Card',
      manual: moneyReport.creditCard || 0,
      expenses: moneyReport.cashOutCard || 0,
      system: counterData.paymentBreakdown.card.amount || 0,
      difference: cardDiff,
      status: cardDiff
    },
    {
      key: 'transfer',
      item: 'Transfer',
      manual: moneyReport.transfer || 0,
      expenses: moneyReport.cashOutTE || 0,
      system: counterData.paymentBreakdown.btransfer.amount || 0,
      difference: transferDiff,
      status: transferDiff
    },
  ];

  const totalManual = totalCash + (moneyReport.creditCard || 0) + (moneyReport.transfer || 0);
  const totalExpenses = (moneyReport.expenses || 0) + (moneyReport.cashOutCard || 0) + (moneyReport.cashOutTE || 0);
  const totalSystem = (counterData.paymentBreakdown.cash.amount || 0) + (counterData.paymentBreakdown.card.amount || 0) + (counterData.paymentBreakdown.btransfer.amount || 0);
  const totalDiff = (totalManual + totalExpenses) - totalSystem;

  // Financial Summary Table
  const financialColumns = [
    { title: 'Item', dataIndex: 'item', key: 'item', render: (text) => <strong>{text}</strong> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (val) => `฿${val.toLocaleString()}` },
  ];

  const financialData = [
    { key: '1', item: 'Expenses', amount: moneyReport.expenses || 0 },
    { key: '2', item: 'Cash Out - CARD', amount: moneyReport.cashOutCard || 0 },
    { key: '3', item: 'Cash Out - Transfer', amount: moneyReport.cashOutTE || 0 },
    { key: '4', item: 'Amount Received Today', amount: moneyReport.amountReceivedToday || 0 },
    { key: '5', item: 'Total Coins Amount', amount: totalCoins },
    { key: '6', item: 'Total Manual Cash Amount', amount: totalCash },
    { key: '7', item: 'Total Photo/Other Amount', amount: moneyReport.denominations.photo || 0 },
  ];

  const grandTotal = totalCoins + totalCash + (moneyReport.denominations.photo || 0);

  return (
    <Modal
      title={
        <span style={{ color: '#f08934', fontWeight: '600', fontSize: '18px' }}>
          <FaEye style={{ marginRight: '8px' }} />
          Review & Compare Details
        </span>
      }
      open={showReviewModal}
      onCancel={() => setShowReviewModal(false)}
      width={1200}
      centered
      footer={[
        <Button key="pdf" type="primary" icon={<FaDownload />} onClick={exportReviewToPDF}>
          Export to PDF
        </Button>,
        <Button key="close" onClick={() => setShowReviewModal(false)}>
          Close
        </Button>
      ]}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {/* Currency and Coin Denominations */}
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card 
            title={<span style={{ color: '#f1842a' }}>💵 Currency Denominations</span>}
            size="small"
            headStyle={{ backgroundColor: '#fff3e6', borderBottom: '2px solid #f1842a' }}
          >
            <Table 
              columns={currencyColumns} 
              dataSource={currencyData} 
              pagination={false} 
              size="small"
              rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : ''}
              summary={() => (
                <Table.Summary.Row style={{ backgroundColor: '#d4edda', fontWeight: 'bold' }}>
                  <Table.Summary.Cell colSpan={2}><strong>Total Cash</strong></Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <strong>฿{totalCash.toLocaleString()}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card 
            title={<span style={{ color: '#e67e22' }}>🪙 Coin Denominations</span>}
            size="small"
            headStyle={{ backgroundColor: '#fff5e6', borderBottom: '2px solid #e67e22' }}
          >
            <Table 
              columns={coinColumns} 
              dataSource={coinData} 
              pagination={false} 
              size="small"
              rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : ''}
              summary={() => (
                <Table.Summary.Row style={{ backgroundColor: '#ffe0b2', fontWeight: 'bold' }}>
                  <Table.Summary.Cell colSpan={2}><strong>Total Coins</strong></Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <strong>฿{totalCoins.toLocaleString()}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card 
            title={<span style={{ color: '#f39c12' }}>📸 Photo/Other Amount</span>}
            size="small"
            headStyle={{ backgroundColor: '#fffaf0', borderBottom: '2px solid #f39c12' }}
          >
            <Table 
              columns={[
                { title: 'Description', dataIndex: 'desc', key: 'desc', render: (text) => <strong>{text}</strong> },
                { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (val) => `฿${val.toLocaleString()}` }
              ]} 
              dataSource={[
                { key: '1', desc: 'Photo/Other', amount: moneyReport.denominations.photo || 0 }
              ]} 
              pagination={false} 
              size="small"
              style={{ backgroundColor: '#fff3cd' }}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Payment Methods - Manual vs System */}
      <Card 
        title={<span style={{ color: '#f08934' }}>💳 Payment Methods - Manual vs System</span>}
        size="small"
        style={{ marginTop: '16px' }}
        headStyle={{ backgroundColor: '#fff7f0', borderBottom: '2px solid #f08934' }}
      >
        <Table 
          columns={paymentColumns} 
          dataSource={paymentData} 
          pagination={false} 
          size="small"
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 'bold' }}>
              <Table.Summary.Cell><strong style={{ color: 'white' }}>GRAND TOTAL</strong></Table.Summary.Cell>
              <Table.Summary.Cell align="right"><strong style={{ color: 'white' }}>฿{totalManual.toLocaleString()}</strong></Table.Summary.Cell>
              <Table.Summary.Cell align="right"><strong style={{ color: 'white' }}>฿{totalExpenses.toLocaleString()}</strong></Table.Summary.Cell>
              <Table.Summary.Cell align="right"><strong style={{ color: 'white' }}>฿{totalSystem.toLocaleString()}</strong></Table.Summary.Cell>
              <Table.Summary.Cell align="right"><strong style={{ color: 'white' }}>฿{Math.abs(totalDiff).toLocaleString()}</strong></Table.Summary.Cell>
              <Table.Summary.Cell align="center">
                <Tag color="default" style={{ backgroundColor: 'white', color: '#4caf50', fontWeight: 'bold' }}>TOTAL</Tag>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      <Divider />

      {/* Financial Summary */}
      <Card 
        title={<span style={{ color: '#f08934' }}>📊 Financial Summary</span>}
        size="small"
        style={{ marginTop: '16px' }}
        headStyle={{ backgroundColor: '#fff7f0', borderBottom: '2px solid #f08934' }}
      >
        <Table 
          columns={financialColumns} 
          dataSource={financialData} 
          pagination={false} 
          size="small"
          rowClassName={(record) => {
            if (record.key === '5' || record.key === '6' || record.key === '7') {
              return 'table-row-highlight';
            }
            return '';
          }}
          summary={() => (
            <>
              <Table.Summary.Row style={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 'bold' }}>
                <Table.Summary.Cell><strong style={{ color: 'white' }}>GRAND TOTAL</strong></Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong style={{ color: 'white' }}>฿{grandTotal.toLocaleString()}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row style={{ backgroundColor: '#fff3e0' }}>
                <Table.Summary.Cell><strong>Pending Bills (System)</strong></Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>฿{(counterData.summary.pendingBills || 0).toLocaleString()}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          )}
        />
      </Card>

      <Divider />

      {/* Grand Totals */}
      <Card 
        title={<span style={{ color: '#f08934' }}>💎 Grand Totals</span>}
        size="small"
        style={{ marginTop: '16px' }}
        headStyle={{ backgroundColor: '#fff7f0', borderBottom: '2px solid #f08934' }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Card size="small" style={{ background: 'linear-gradient(135deg, #D0FFFB, #BAEECE)', textAlign: 'center' }}>
              <Statistic
                title="Manual Cash Count"
                value={totalCash}
                precision={0}
                prefix="฿"
                valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" style={{ background: 'linear-gradient(135deg, #4caf50, #81c784)', textAlign: 'center' }}>
              <Statistic
                title="Grand Total (Cash + Coins + Photo)"
                value={grandTotal}
                precision={0}
                prefix="฿"
                valueStyle={{ color: 'white', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Alert */}
      <div style={{ 
        marginTop: '16px', 
        padding: '12px', 
        backgroundColor: '#e6f7ff', 
        border: '1px solid #91d5ff', 
        borderRadius: '4px',
        color: '#0050b3'
      }}>
        <strong>ℹ️ Note:</strong> Please review all entries carefully before closing the counter. 
        Any mismatches between manual entry and system data should be investigated.
      </div>

      <style jsx>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-highlight {
          background-color: #e8f5e9;
        }
      `}</style>
    </Modal>
  );
};

export default ReviewModalAntD;
